import { BaseService } from '../../service/base';

export enum PMType {
    HOURS = 0,
    ODOMETER = 1,
    DATE = 2,
}

export interface PMTypeDTO {
    id: string;
    name: string;
    type: number;
    interval: number;
    threshold: number;
    email: string[];
    month: number;
    day: number;

    odometerIntervalKm: number;
    odometerAlertKm: number;
    hoursInterval: number;
    hoursAlert: number;
    daysInterval: number;
    daysAlert: number;
    dateInterval: number;
    dateAlert: number;
    dateMonth: number;
    dateDay: number;
}

export interface PMWork {
    id: string;
    date: string;
    description: string;
    cost: number;
}

export interface PMDTO {
    id: string;
    currentHours: number;
    currentOdometer: number;
    currentDays: number;
    dueAt: number;
    dueIn: number;
    status: number;
    measurementType: number;
    name: string;
    assetId: string;
    work: PMWork[];
    pmtypeid: string;

    dateDueAt: number;
    daysDueAt: number;
    odometerDueAt: number;
    hoursDueAt: number;

    vehicleOdoDueAtKm: number;
    vehicleOdoDueAtMiles: number;
    vehicleHoursDueAt: number;
}

export interface PMInfoDTO {
    hours: number;
    odometerKm: number;
}

export interface PMCompletedDTO {
    completedId: string;
    assetId: string;
    completedDate: string;
    work: PMWork[];
    pmname: string;
}

/**
 *
 */
export interface PMCompleteRequestDTO {
    pmId: string;
    completedDate: number;
    dueAtKm: number;
    dueAtHours: number;
    dueAtDays: number;
    dueAtDate: number;

    completedOdometerKm: number;
    completedHours: number;

    currentOdometerKm: number;
    currentHours: number;

    vehicleOdoDueAtKm: number;
}

/**
 *
 */
export class PreventativeMaintenanceService extends BaseService {
    public pmCache?: Promise<PMDTO[]>;
    public pmTypeCache?: Promise<PMTypeDTO[]>;
    private pmTypeMapPromise?: Promise<Map<number, PMTypeDTO>>;

    public dirty = false;

    constructor() {
        super();
        console.log('PM Service Starting up');
    }

    getPMFileUploadURL(workItemId: string): string {
        return this.url + '/api/' + this.orgid + '/pm/workItem/' + workItemId + '/upload';
    }

    public cachedFetchData(): Promise<PMDTO[]> {
        // Check if the data for the given id is already cached
        if (this.pmCache && this.dirty == false) {
            console.log(`Returning cached data`);
            return this.pmCache;
        }

        // If not cached, fetch the data and store the promise in the cache
        const promise = this.fetchData();
        this.pmCache = promise;
        return promise;
    }

    private fetchData(): Promise<PMDTO[]> {
        const url = this.url + '/api/' + this.orgid + '/pm';
        return fetch(url, {
            method: 'GET',
            headers: this.headers(),
        }).then((response) => {
            this.dirty = false;
            return response.json();
        });
    }

    async createNewPMFromType(pid: string, assetid: string, odoKm: number, hours: number, days?: number, date?: number, currentOdometerKm?: number, currentHours?: number) {
        const data = {
            asset: assetid,
            dueHours: hours,
            dueOdoKm: odoKm,
            dueDays: days,
            dueDate: date,
            currentOdometerKm: currentOdometerKm,
            currentHours: currentHours,
        };

        const response = await this.post<PMDTO>(this.url + '/api/' + this.orgid + '/pm/type/' + pid, data);
        return response;
    }

    async createNewPMFromTypeOld(pid: string, assetid: string, hours: string | undefined, odo_km: number | undefined) {
        this.pmCache = undefined;

        console.log('Create new PM:');
        console.log('Hours:' + hours);
        console.log('Odometer:  ' + odo_km);

        const data = {
            asset: assetid,
            hours: hours,
            odo: odo_km,
        };

        const response = await this.post<PMDTO>(this.url + '/api/' + this.orgid + '/pm/type/' + pid, data);
        return response;
    }

    async createNewPM(id: string) {
        const response = await this.post<PMDTO>(this.url + '/api/' + this.orgid + '/pm', id);
        return response;
    }

    async removePM(id: string) {
        const response = await this.delete<PMDTO>(this.url + '/api/' + this.orgid + '/pm', id);
        return response;
    }

    async loadPM(_refresh: boolean) {
        const response = await this.get<PMDTO[]>(this.url + '/api/' + this.orgid + '/pm');
        return response;
    }

    async updateDTO(pm: PMDTO) {
        const response = await this.put<PMDTO>(this.url + '/api/' + this.orgid + '/pm', pm);
        return response;
    }

    async removeAsset(id: string) {
        const response = await this.delete<PMDTO>(this.url + '/api/' + this.orgid + '/pm/asset', id);
        return response;
    }

    async updateWork(id: string, result: PMWork[]) {
        // Clear out the cache to force reload
        this.pmCache = undefined;

        const response = await this.post<unknown>(this.url + '/api/' + this.orgid + '/pm/' + id + '/work', result);
        return response;
    }

    /** Save a PM type to the database
     *
     * @param dto The PM Type DTO to save
     * @returns The promise
     */
    async savePMType(dto: PMTypeDTO) {
        const response = await this.post<PMTypeDTO>(this.url + '/api/' + this.orgid + '/pm/type', dto);
        return response;
    }

    /** Delete a PM Type */
    async deletePMType(pmtypeid: string) {
        const response = await this.delete<PMTypeDTO>(this.url + '/api/' + this.orgid + '/pm/type', pmtypeid);
        return response;
    }

    public getTypes(refresh: boolean): Promise<PMTypeDTO[]> {
        // Check if the data for the given id is already cached

        if (this.pmTypeCache && refresh == false) {
            console.log(`Returning cached data`);
            return this.pmTypeCache;
        }

        // If not cached, fetch the data and store the promise in the cache
        const promise = this.fetchTypes();
        this.pmTypeCache = promise;
        return promise;
    }

    private fetchTypes(): Promise<PMTypeDTO[]> {
        const url = this.url + '/api/' + this.orgid + '/pm/type';
        return fetch(url, {
            method: 'GET',
            headers: this.headers(),
        }).then((response) => {
            return response.json();
        });
    }

    public async completedRequestPM(pid: string, dto: PMCompleteRequestDTO) {
        const response = await this.post<unknown>(this.url + '/api/' + this.orgid + '/pm/' + pid + '/completed', dto);
        return response;
    }

    /**
     *
     * @param pid
     * @param work
     */
    public async completePM(pid: string, work: PMWork[]) {
        const response = await this.post<unknown>(this.url + '/api/' + this.orgid + '/pm/' + pid + '/complete', work);
        return response;
    }

    public async fetchHistory(aid: string) {
        const response = await this.get<PMCompletedDTO[]>(this.url + '/api/' + this.orgid + '/pm/history/' + aid);
        return response;
    }

    public async loadTopTen() {
        const response = await this.get<PMDTO[]>(this.url + '/api/' + this.orgid + '/pm/upcoming');
        return response;
    }

    public async getInfoAt(attime: string, assetid: string) {
        const response = await this.get<PMInfoDTO>(this.url + '/api/' + this.orgid + '/pm/' + assetid + '/info?at=' + attime);
        return response;
    }

    public async saveNote(id: string, notesItem: PMWork) {
        const response = await this.post<unknown>(this.url + '/api/' + this.orgid + '/pm/' + id + '/note', notesItem);
        return response;
    }

    public async deleteNote(pmid: string, id: string) {
        const response = await this.delete<unknown>(this.url + '/api/' + this.orgid + '/pm/' + pmid + '/note/' + id, undefined);
        return response;
    }

    public async deleteCompletedPM(pmid: string) {
        const response = await this.delete<unknown>(this.url + '/api/' + this.orgid + '/pm/completed/' + pmid, undefined);
        return response;
    }

    public async setVehicleDueAtOdometer(pmInstanceId: string, newOdometerValueKm: number) {
        const response = await this.get<unknown>(this.url + '/api/' + this.orgid + '/pm/' + pmInstanceId + '/due-odometer?value=' + newOdometerValueKm);
        return response;
    }

    /** Sets the vehicle due hours for a PM instance
     *
     * @param pmInstanceId The ID of the PM instance for which to set the due hours.
     * @param newHoursValue The new due hours value to set for the PM instance.
     * @returns A promise resolving to the response of the API call.
     */
    public async setVehicleDueAtHours(pmInstanceId: string, newHoursValue: number) {
        const response = await this.get<unknown>(this.url + '/api/' + this.orgid + '/pm/' + pmInstanceId + '/due-hoursr?value=' + newHoursValue);
        return response;
    }
}
